import { useEffect, useRef } from 'react';
import { prewarmWorkbench, shouldSchedulePrewarm } from '@ideoz/workbench';

/**
 * Schedule WebContainer prewarm during browser idle time. Called from the conversation page
 * only (not the app shell), so browsing Work Scopes, Home or Artifacts never boots a
 * WebContainer (WC-2). `shouldSchedulePrewarm` further limits it to the `full` device tier.
 */
export function useWorkbenchPrewarm(enabled: boolean): void {
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled || startedRef.current) return;
    if (!shouldSchedulePrewarm()) return;

    startedRef.current = true;
    const controller = new AbortController();

    const run = (): void => {
      void prewarmWorkbench({ signal: controller.signal });
    };

    // Read through a typed view rather than `'requestIdleCallback' in window`: that narrows
    // `window` itself, and every later use of it in this function becomes `never`.
    const idle = window as Window &
      typeof globalThis & {
        requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
        cancelIdleCallback?: (handle: number) => void;
      };
    if (typeof idle.requestIdleCallback === 'function') {
      const id = idle.requestIdleCallback(run, { timeout: 8_000 });
      return () => {
        controller.abort();
        idle.cancelIdleCallback?.(id);
      };
    }

    const timer = window.setTimeout(run, 2_000);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [enabled]);
}
